
      
"use client";

import { Suspense, useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Loader2, ShoppingCart } from 'lucide-react';
import ProductsGridAndFilters from '@/app/product/ProductsGridAndFilters';
import { collection, getDocs, doc, getDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Product, Category, Brand, StoreSettings, ProductExpress, DeliverySettings, User as AppUser } from '@/lib/types';
import { useExpressCart } from '@/context/ExpressCartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSearchParams } from 'next/navigation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


// This function can remain as it's a helper, not a component.
async function getWolfExpressData(partnerId?: string | null) {
    try {
        let expressQuery;
        if (partnerId) {
            expressQuery = query(collection(db, 'productExpress'), where('partnerId', '==', partnerId), where('stock', '>', 0));
        } else {
            expressQuery = query(collection(db, 'productExpress'), where('stock', '>', 0));
        }

        const [
            storeSettingsDoc,
            brandsSnapshot,
            categoriesSnapshot,
            expressStockSnapshot,
            deliverySettingsDoc
        ] = await Promise.all([
            getDoc(doc(db, 'settings', 'store')),
            getDocs(query(collection(db, 'brands'), orderBy('name'))),
            getDocs(query(collection(db, 'categories'), orderBy('name'))),
            getDocs(expressQuery),
            getDoc(doc(db, 'settings', 'delivery'))
        ]);

        const storeSettings = storeSettingsDoc.exists() ? storeSettingsDoc.data() as StoreSettings : null;
        const brands = brandsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Brand));
        const categories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category));
        const deliverySettings = deliverySettingsDoc.exists() ? deliverySettingsDoc.data() as DeliverySettings : null;
        
        if (expressStockSnapshot.empty) {
            return { allProducts: [] as Product[], categories, brands, storeSettings, deliverySettings, error: null };
        }

        const expressStockMap = new Map<string, { partnerId: string; stock: number; price: number; originalPrice?: number }[]>();
        const productIdsWithStock = new Set<string>();

        expressStockSnapshot.forEach(doc => {
            const data = doc.data() as ProductExpress;
            productIdsWithStock.add(data.productId);
            const key = `${data.productId}_${data.variantId}`;
            const entry = expressStockMap.get(key) || [];
            entry.push({ partnerId: data.partnerId, stock: data.stock, price: data.price, originalPrice: data.originalPrice });
            expressStockMap.set(key, entry);
        });
        
        if (productIdsWithStock.size === 0) {
            return { allProducts: [] as Product[], categories, brands, storeSettings, deliverySettings, error: null };
        }

        const idChunks: string[][] = [];
        const productIdsArray = Array.from(productIdsWithStock);
        for (let i = 0; i < productIdsArray.length; i += 30) {
            idChunks.push(productIdsArray.slice(i, i + 30));
        }

        const productPromises = idChunks.map(chunk => 
            getDocs(query(collection(db, 'products'), where('__name__', 'in', chunk)))
        );
        const productSnapshots = await Promise.all(productPromises);
        
        const fetchedProducts = new Map<string, Product>();
        productSnapshots.forEach(snapshot => {
            snapshot.forEach(doc => {
                fetchedProducts.set(doc.id, { id: doc.id, ...doc.data() } as Product);
            });
        });

        const expressProducts = Array.from(fetchedProducts.values())
            .map((p): Product | null => {
                if (!p.variants) return null;

                const updatedVariants = p.variants.map(v => {
                    const key = `${p.id}_${v.id}`;
                    const expressEntries = expressStockMap.get(key);
                    
                    if (!expressEntries || expressEntries.length === 0) {
                        return { ...v, stock: 0 }; 
                    }
                    
                    const bestOffer = expressEntries.sort((a, b) => a.price - b.price)[0];
                    
                    return {
                        ...v,
                        stock: bestOffer.stock,
                        price: bestOffer.price,
                        originalPrice: bestOffer.originalPrice,
                        addedFrom: bestOffer.partnerId 
                    };
                }).filter(v => v.stock > 0);

                if (updatedVariants.length === 0) {
                    return null;
                }
                
                return { 
                    ...p, 
                    variants: updatedVariants,
                    isExpress: true,
                    stock: updatedVariants.reduce((acc, v) => acc + (v.stock || 0), 0)
                };
            })
            .filter((p): p is Product => p !== null);

        return { allProducts: expressProducts, categories, brands, storeSettings, deliverySettings, error: null };

    } catch (error) {
        console.error("Error fetching data for Wolf Express page:", error);
        return { allProducts: [] as Product[], categories: [], brands: [], storeSettings: null, deliverySettings: null, error: "Failed to load products." };
    }
}


function WolfExpressContent({
  searchParams,
  partnerId
}: {
  searchParams: { [key: string]: string | string[] | undefined };
  partnerId?: string | null;
}) {
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { cartItems } = useExpressCart();

    useEffect(() => {
        getWolfExpressData(partnerId).then(data => {
            setAllProducts(data.allProducts);
            setError(data.error);
            setIsLoading(false);
        });
    }, [partnerId]);
    
    const cartItemCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
    const searchQuery = typeof searchParams.q === 'string' ? searchParams.q : null;

    if (isLoading) {
        return <div className="flex justify-center py-16"><Loader2 className="h-16 w-16 animate-spin" /></div>
    }

    if (error) {
        return <div className="text-center text-destructive py-16">{error}</div>;
    }
    
    if(allProducts.length === 0 && !isLoading){
        return (
            <div className="text-center py-16">
                <h2 className="text-2xl font-bold font-headline">A Express Está Pausada No Momento</h2>
                <p className="text-muted-foreground mt-2">
                    Veja Nossos Diversos Produtos e Novidades Com Entrega Grátis Para Jampa Clicando.
                </p>
                 <Button asChild className="mt-4">
                    <Link href="/products">
                        Ver todos os produtos
                    </Link>
                </Button>
            </div>
        )
    }

    return (
        <>
            <ProductsGridAndFilters 
                searchQuery={searchQuery}
                preloadedProducts={allProducts}
            />
            {cartItemCount > 0 && (
                <Link href="/cartexpress" className="fixed bottom-24 right-6 z-50">
                    <Button
                        size="lg"
                        className="relative h-16 w-16 rounded-full shadow-2xl"
                    >
                        <ShoppingCart className="h-7 w-7" />
                        <Badge className="absolute -top-1 -right-1 h-7 w-7 justify-center text-base p-0">
                            {cartItemCount}
                        </Badge>
                        <span className="sr-only">Ver Carrinho Express</span>
                    </Button>
                </Link>
            )}
        </>
    );
}

function WolfExpressPageWrapper({
  searchParams,
}: {
  searchParams: { [key: string]: string | string[] | undefined };
}) {
    const [expressFee, setExpressFee] = useState('20.00');
    const [partner, setPartner] = useState<AppUser | null>(null);
    const [isPartnerPaused, setIsPartnerPaused] = useState(false);
    const [isPartnerLoading, setIsPartnerLoading] = useState(true);

    const partnerId = Array.isArray(searchParams.partnerId) ? searchParams.partnerId[0] : searchParams.partnerId;

    useEffect(() => {
        const fetchPartnerStatus = async () => {
            if (!partnerId) {
                setIsPartnerLoading(false);
                return;
            }
            try {
                const partnerDoc = await getDoc(doc(db, 'users', partnerId));
                if (partnerDoc.exists()) {
                    const partnerData = partnerDoc.data() as AppUser;
                    setPartner(partnerData);
                    setIsPartnerPaused(partnerData.isSalesPaused || false);
                }
            } catch (e) {
                console.error("Error fetching partner status", e);
            } finally {
                setIsPartnerLoading(false);
            }
        };

        fetchPartnerStatus();

        const fetchFee = async () => {
            const deliverySettingsDoc = await getDoc(doc(db, 'settings', 'delivery'));
            if (deliverySettingsDoc.exists()) {
                const settings = deliverySettingsDoc.data() as DeliverySettings;
                setExpressFee(settings.wolfExpressFee?.toFixed(2) || '20.00');
            }
        };
        fetchFee();
    }, [partnerId]);

    return (
        <>
            {isPartnerPaused && (
                <AlertDialog open={isPartnerPaused} onOpenChange={setIsPartnerPaused}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Entrega Express Pausada</AlertDialogTitle>
                      <AlertDialogDescription>
                        As entregas express deste parceiro estão pausadas no momento. Você pode conferir nossos outros produtos com entrega agendada.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogAction asChild>
                        <Link href="/category/pods-descartaveis">Ver Produtos</Link>
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
            )}
            <section className="relative h-[50vh] flex items-center justify-center text-center text-white overflow-hidden -mt-12">
                <Image 
                src={"https://images.unsplash.com/photo-1621282643542-951da1993437?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"}
                alt="Entrega Expressa"
                fill
                className="absolute inset-0 z-0 object-cover"
                data-ai-hint="motorcycle delivery city"
                priority
                />
                <div className="absolute inset-0 bg-black/60 z-0"></div>
                <div className="z-10 p-8 rounded-lg">
                <h1 className="text-4xl md:text-7xl font-bold font-headline animate-fade-in-down drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-300 text-transparent bg-clip-text">
                    Wolf Express
                </h1>
                <p className="mt-4 text-lg md:text-2xl max-w-2xl mx-auto animate-fade-in-up text-white/90 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">
                    Receba seus produtos em até 30 minutos.
                    <br/>
                    <span className="text-sm md:text-base text-white/80">
                        Disponível no tempo estimado apenas para pagamentos online e Somente Para Região Praias João Pessoa e Intermares. Demais Localidades de JOÃO PESSOA o Envio é Imediato - Taxa Express: R$ {expressFee}
                    </span>
                </p>
                </div>
            </section>
            <div className="container mx-auto py-12">
                <Suspense fallback={<div className="flex justify-center py-16"><Loader2 className="h-16 w-16 animate-spin" /></div>}>
                   <WolfExpressContent searchParams={searchParams} partnerId={partnerId} />
                </Suspense>
            </div>
        </>
    );
}

export default function WolfExpressPage({
  searchParams,
}: {
  searchParams: { [key: string]: string | string[] | undefined };
}) {
    return (
        <Suspense fallback={<div className="flex h-full min-h-[50vh] items-center justify-center"><Loader2 className="h-16 w-16 animate-spin" /></div>}>
            <WolfExpressPageWrapper searchParams={searchParams} />
        </Suspense>
    )
}

    

